import React from 'react';
import { Box, Container, Typography, Stack, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import ChatImgDesktop from '../../assets/ChatSection.png';
import ChatImgMobile from '../../assets/ChatSectionMobile.png';
import Logo from '../../assets/Text-WFHCafe_WObg.png';

interface ChatSectionProps {
  isDark: boolean;
}

const ChatSection: React.FC<ChatSectionProps> = ({ isDark }) => {
  const navigate = useNavigate();
  
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: isDark ? '#000000' : '#FAFAFA',
        minHeight: { xs: 'auto', md: '100vh' },
      }}
    >
      {/* Image container - full width */}
      <Box
        sx={{
          width: '100vw',
          maxWidth: '100%',
          height: 'auto',
          minHeight: { xs: '100vh', md: 'auto' },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Image - Different for mobile/desktop */}
        <Box
          component="img"
          src={ChatImgDesktop}
          alt="Community Chat"
          sx={{
            width: '100%',
            height: 'auto',
            minHeight: { xs: '100vh', md: 'auto' },
            objectFit: 'cover',
            // Hide desktop image on mobile
            display: { xs: 'none', md: 'block' },
          }}
        />
        <Box
          component="img"
          src={ChatImgMobile}
          alt="Community Chat Mobile"
          sx={{
            width: '100%',
            height: 'auto',
            minHeight: { xs: '100vh', md: 'auto' },
            objectFit: 'cover',
            // Hide mobile image on desktop
            display: { xs: 'block', md: 'none' },
          }}
        />
        
        {/* Content Container - Different alignment for mobile vs desktop */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '30px', md: 0 }, // Move content higher on mobile
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: 3, md: 8 }, // Reduced gap on mobile
              py: { xs: 2, md: 10 }, // Reduced padding on mobile
              px: { xs: 2, sm: 4, md: 0 },
            }}>
              {/* Left Content */}
              <Box sx={{ 
                flex: 1,
                textAlign: 'left',
                color: '#051814',
                maxWidth: { xs: '100%', md: '600px' },
              }}>
                {/* Logo - Higher on mobile */}
                <Box
                  component="img"
                  src={Logo}
                  alt="WFHCafe"
                  sx={{
                    width: '100%',
                    maxWidth: { xs: '250px', md: '400px' }, // Smaller on mobile
                    mb: { xs: 2, md: 5 }, // Reduced margin on mobile
                    filter: 'drop-shadow(0 4px 20px rgba(5, 24, 20, 0.2))',
                    opacity: 0.95,
                  }}
                />

                {/* Paragraphs - Desktop only */}
                <Stack spacing={3} sx={{ mb: { xs: 4, md: 6 }, display: { xs: 'none', md: 'block' } }}>
                  <Typography
                    sx={{
                      color: '#051814',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      lineHeight: 1.7,
                      fontWeight: 400,
                    }}
                  >
                    Chat with remote professionals across multiple cafés simultaneously. 
                    Whether you're in a cozy corner coffee shop or a bustling city café, 
                    stay connected with our community wherever you choose to work.
                  </Typography>

                  <Typography
                    sx={{
                      color: '#051814',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      lineHeight: 1.7,
                      fontWeight: 400,
                    }}
                  >
                    Engage in real-time conversations with like-minded professionals. 
                    Share insights, collaborate on projects, and discover new opportunities 
                    while enjoying the café atmosphere that inspires productivity.
                  </Typography>
                </Stack>

                {/* Mobile Text - Only 2 lines, positioned higher */}
                <Box sx={{ 
                  display: { xs: 'block', md: 'none' }, 
                  mb: 3,
                  mt: 1, // Added top margin
                }}>
                  <Typography
                    sx={{
                      color: '#051814',
                      fontSize: '0.95rem', // Slightly smaller
                      lineHeight: 1.4, // Tighter line height
                      fontWeight: 400,
                      mb: 0.5, // Reduced margin between lines
                    }}
                  >
                    Chat with remote professionals across multiple cafés.Engage in real-time conversations while you work.
                  </Typography>
                </Box>

                {/* CTA Button with Glass Effect - Hidden on mobile */}
                {/* <Button
                              variant="contained"
                              size="large"
                              onClick={() => navigate('/explore')}
                              endIcon={<ArrowForward sx={{ fontSize: { xs: '1rem', md: '1.5rem' } }} />}
                              sx={{
                                minWidth: { xs: 'auto', sm: '200px' },
                                px: { xs: 2, sm: 5 },
                                py: { xs: 1.5, sm: 2 },
                                borderRadius: '100px',
                                background: isDark 
                                  ? `linear-gradient(135deg, rgba(5, 24, 20,1) 0%, rgba(15, 59, 46, 1) 100%)`
                                  : `linear-gradient(135deg, rgba(85, 85, 85, 1) 0%, rgba(184, 184, 184, 1) 100%)`,
                                color: isDark ? '#C4B66F' : 'white', 
                                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                textTransform: 'none',
                                backdropFilter: 'blur(10px)',
                                border: isDark ? '1px solid rgba(196, 182, 111, 0.3)' : 'none',
                                ...discordFont,
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 10px 20px ${alpha('#0C3B2E', 0.5)}`,
                                },
                                transition: 'all 0.3s ease',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Join Conversation
                            </Button> */}
              </Box>

              {/* Right Side - Empty space for image background */}
              <Box sx={{ 
                flex: 1, 
                display: { xs: 'none', md: 'block' },
                minHeight: '500px',
              }} />
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatSection;